import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Mail, Clock, Languages, ChevronDown } from "lucide-react";
import { PHONE_HREF, WA_HREF, EMAIL_HREF, WhatsAppIcon, scrollToId } from "@/lib/site";
import { T, useLang } from "@/lib/i18n";
import WeatherBadge from "@/components/WeatherBadge";

const HERO_IMG = "/images/hero-salzburg.webp";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.15 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: 0.7 + i * 0.12, duration: 0.7, ease: "easeOut" } }),
};

const BADGE_ICONS = [Clock, Languages];

export default function Hero() {
  const ref = useRef(null);
  const { lang } = useLang();
  const t = T[lang].hero;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  return (
    <section ref={ref} id="home" data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt="Hohensalzburg Fortress above Salzburg old town"
          fetchPriority="high"
          decoding="async"
          className="w-full h-[120%] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1613]/60 via-[#1A1613]/40 to-[#1A1613]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1613]/65 via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ y: contentY }} className="max-w-7xl mx-auto px-5 lg:px-10 pt-36 pb-20 lg:pb-28 w-full">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          data-testid="hero-overline"
          className="font-mono-label text-xs uppercase tracking-[0.28em] text-[#C5A059] mb-6"
        >
          {t.overline}
        </motion.p>

        <h1 data-testid="hero-headline" className="font-serif-display font-semibold tracking-tight leading-[1.04] text-stone-100 text-5xl sm:text-6xl lg:text-[5.2rem] max-w-4xl">
          <span className="mask-line">
            <motion.span className="block" custom={0} variants={lineVariants} initial="hidden" animate="show" key={`${lang}-a`}>
              {t.h1a}
            </motion.span>
          </span>
          <span className="mask-line">
            <motion.span className="block" custom={1} variants={lineVariants} initial="hidden" animate="show" key={`${lang}-b`}>
              {t.h1bPre}<em className="not-italic text-[#C5A059]">{t.h1bGold}</em>{t.h1bPost}
            </motion.span>
          </span>
        </h1>

        <motion.p
          custom={0}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          data-testid="hero-subheadline"
          className="mt-7 max-w-xl text-base sm:text-lg text-stone-400 leading-relaxed"
        >
          {t.sub}
        </motion.p>

        <motion.div custom={1} variants={fadeUp} initial="hidden" animate="show" className="mt-9 flex flex-wrap items-center gap-4">
          <a
            data-testid="hero-call-button"
            href={PHONE_HREF}
            className="group flex items-center gap-3 rounded-full bg-[#C5A059] text-[#141312] px-7 py-4 text-sm font-semibold tracking-wide hover:bg-[#D9B877] transition-colors duration-300 shadow-lg shadow-[#C5A059]/20"
          >
            <Phone className="w-4 h-4 transition-transform duration-300 group-hover:rotate-12" />
            {t.call}
          </a>
          <a
            data-testid="hero-whatsapp-button"
            href={WA_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 rounded-full bg-[#25D366] text-white px-7 py-4 text-sm font-semibold tracking-wide hover:bg-[#1DA851] transition-colors duration-300 shadow-lg shadow-emerald-600/20"
          >
            <WhatsAppIcon className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
            {t.whatsapp}
          </a>
          <a
            data-testid="hero-email-button"
            href={EMAIL_HREF}
            className="group flex items-center gap-3 rounded-full bg-[#D64545] text-white px-7 py-4 text-sm font-semibold tracking-wide hover:bg-[#B93737] transition-colors duration-300 shadow-lg shadow-red-900/25"
          >
            <Mail className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
            E-Mail
          </a>
          <button
            data-testid="hero-tours-button"
            onClick={() => scrollToId("destinations")}
            className="flex items-center gap-3 rounded-full border border-stone-500/70 text-base font-semibold text-stone-200 hover:text-[#C5A059] hover:border-[#C5A059]/70 transition-colors duration-300 px-7 py-4"
          >
            {t.explore}
            <ChevronDown className="w-5 h-5" />
          </button>
        </motion.div>

        <motion.div custom={2} variants={fadeUp} initial="hidden" animate="show" className="mt-14 flex flex-wrap gap-x-10 gap-y-4" data-testid="hero-trust-badges">
          <WeatherBadge />
          {t.badges.map((label, i) => {
            const Icon = BADGE_ICONS[i];
            return (
              <div key={label} className="flex items-center gap-2.5 text-sm text-stone-300">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 border border-[#C5A059]/30">
                  <Icon className="w-4 h-4 text-[#C5A059]" />
                </span>
                {label}
              </div>
            );
          })}
        </motion.div>
      </motion.div>
    </section>
  );
}
